import { FormEvent, ReactNode, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ds2/components";
import { Plus } from "lucide-react";
import { Separator } from "../../../ds2/components";

import "./AppPayResources.css";
import "../app-payroll/AppPayroll.css";

const getStatusBadge = (status: string) => {
  switch (status.toLowerCase()) {
    case "complete":
    case "live":
      return <Badge className="status-badge-success">{status}</Badge>;
    case "in_progress":
      return <Badge className="status-badge-info">In Progress</Badge>;
    case "pending":
      return <Badge className="status-badge-warning">Pending</Badge>;
    case "active":
      return <Badge className="status-badge-success">Active</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

export function AppPayConfig() {
  return (
    <div className="app-payroll">
      <div className="app-payroll-content">
        <div className="company-overview-container section-spacing">
          <div className="company-overview-stack-large">
            <Tabs
              defaultValue="schedules"
              className="company-overview-stack-large">
              <TabsList>
                <TabsTrigger value="schedules">Pay Schedules</TabsTrigger>
                <TabsTrigger value="earnings">Earning Codes</TabsTrigger>

                <TabsTrigger value="taxes">Tax Settings</TabsTrigger>
                <TabsTrigger value="general">General</TabsTrigger>
              </TabsList>

              {/* Pay Schedules */}
              <TabsContent
                value="schedules"
                className="company-overview-stack-large">
                <Card>
                  <CardHeader>
                    <div className="card-header-with-actions">
                      <div>
                        <CardTitle>Pay Schedules</CardTitle>
                        <CardDescription>
                          Manage pay schedules for different employee groups
                        </CardDescription>
                      </div>
                      <Button size="sm" variant="secondary">
                        <Plus className="icon-small icon-spacing" />
                        Add Schedule
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Frequency</TableHead>
                          <TableHead>Next Pay Date</TableHead>
                          <TableHead>Employees</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="company-overview-text">
                            Bi-weekly Salaried
                          </TableCell>
                          <TableCell>
                            <Badge className="status-badge-success badge-compact">
                              Bi-weekly
                            </Badge>
                          </TableCell>
                          <TableCell className="company-overview-text">
                            Nov 28, 2025
                          </TableCell>
                          <TableCell className="company-overview-text">
                            35
                          </TableCell>
                          <TableCell>
                            <Badge className="status-badge-success badge-compact">
                              Active
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button variant="secondary" size="sm">
                              Edit
                            </Button>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="company-overview-text">
                            Semi-monthly Salaried
                          </TableCell>
                          <TableCell>
                            <Badge className="status-badge-purple badge-compact">
                              Semi-monthly
                            </Badge>
                          </TableCell>
                          <TableCell className="company-overview-text">
                            Nov 30, 2025
                          </TableCell>
                          <TableCell className="company-overview-text">
                            25
                          </TableCell>
                          <TableCell>
                            <Badge className="status-badge-success badge-compact">
                              Active
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button variant="secondary" size="sm">
                              Edit
                            </Button>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="company-overview-text">
                            Weekly Hourly
                          </TableCell>
                          <TableCell>
                            <Badge className="status-badge-info badge-compact">
                              Weekly
                            </Badge>
                          </TableCell>
                          <TableCell className="company-overview-text">
                            Nov 21, 2025
                          </TableCell>
                          <TableCell className="company-overview-text">
                            15
                          </TableCell>
                          <TableCell>
                            <Badge className="status-badge-success badge-compact">
                              Active
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button variant="secondary" size="sm">
                              Edit
                            </Button>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Earning Codes */}
              <TabsContent
                value="earnings"
                className="company-overview-stack-large">
                <Card>
                  <CardHeader>
                    <div className="card-header-with-actions">
                      <div>
                        <CardTitle>Earning Codes</CardTitle>
                        <CardDescription>
                          Configure earning types and multipliers
                        </CardDescription>
                      </div>
                      <Button size="sm" variant="secondary">
                        <Plus className="icon-small icon-spacing" />
                        Add Earning Code
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="company-overview-stack-compact">
                      {[
                        {
                          name: "Regular Salary",
                          type: "Salary",
                          default: true,
                        },
                        { name: "Hourly Wages", type: "Hourly", default: true },
                        {
                          name: "Overtime",
                          type: "Hourly",
                          multiplier: "1.5x",
                        },
                        {
                          name: "Double Time",
                          type: "Hourly",
                          multiplier: "2.0x",
                        },
                        { name: "Bonus", type: "Supplemental", default: false },
                        {
                          name: "Commission",
                          type: "Supplemental",
                          default: false,
                        },
                      ].map((earning, i) => (
                        <div key={i} className="earning-code-item">
                          <div className="earning-code-info">
                            <span className="company-overview-text">
                              {earning.name}
                            </span>
                            {earning.default && (
                              <Badge
                                variant="outline"
                                className="badge-compact">
                                Default
                              </Badge>
                            )}
                            {earning.multiplier && (
                              <Badge className="status-badge-info badge-compact">
                                {earning.multiplier}
                              </Badge>
                            )}
                          </div>
                          <div className="earning-code-actions">
                            <span className="company-overview-text company-overview-text-muted">
                              {earning.type}
                            </span>
                            <Button variant="secondary" size="sm">
                              Edit
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Tax Settings */}
              <TabsContent
                value="taxes"
                className="company-overview-stack-large">
                <Card>
                  <CardHeader>
                    <CardTitle>Tax Configuration</CardTitle>
                    <CardDescription>
                      Federal and state tax settings
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="company-overview-stack-large">
                      <div>
                        <h4 className="tax-settings-title">
                          Federal Tax Settings
                        </h4>
                        <div className="tax-settings-grid">
                          <div className="company-overview-stack-tight">
                            <label className="company-overview-text company-overview-text-muted">
                              Federal EIN
                            </label>
                            <p>12-1234567</p>
                          </div>
                          <div className="company-overview-stack-tight">
                            <label className="company-overview-text company-overview-text-muted">
                              Deposit Schedule
                            </label>
                            <p>Semi-weekly</p>
                          </div>
                        </div>
                      </div>

                      <Separator />

                      <div>
                        <h4 className="tax-settings-title">
                          State Tax Registrations
                        </h4>
                        <div className="company-overview-stack-compact">
                          {[
                            {
                              state: "California",
                              id: "1234567-890",
                              status: "Active",
                            },
                            {
                              state: "New York",
                              id: "9876543-210",
                              status: "Active",
                            },
                            {
                              state: "Texas",
                              id: "5555555-555",
                              status: "Pending",
                            },
                          ].map((state, i) => (
                            <div key={i} className="state-registration-item">
                              <div>
                                <p className="company-overview-text">
                                  {state.state}
                                </p>
                                <p className="company-overview-text-small">
                                  ID: {state.id}
                                </p>
                              </div>
                              {getStatusBadge(state.status)}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* General Settings */}
              <TabsContent
                value="general"
                className="company-overview-stack-large">
                <Card>
                  <CardHeader>
                    <CardTitle>General Settings</CardTitle>
                    <CardDescription>
                      Payroll processing and system preferences
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="company-overview-stack-large">
                      <div className="general-settings-grid">
                        <div className="general-setting-item">
                          <span className="company-overview-text company-overview-text-muted">
                            Processing Period
                          </span>
                          <span className="company-overview-text">
                            Third Day
                          </span>
                        </div>
                        <div className="general-setting-item">
                          <span className="company-overview-text company-overview-text-muted">
                            Default Payment Method
                          </span>
                          <span className="company-overview-text">
                            Direct Deposit
                          </span>
                        </div>
                        <div className="general-setting-item">
                          <span className="company-overview-text company-overview-text-muted">
                            Pay Stub Delivery
                          </span>
                          <span className="company-overview-text">Email</span>
                        </div>
                        <div className="general-setting-item">
                          <span className="company-overview-text company-overview-text-muted">
                            Time Tracking
                          </span>
                          <Badge className="status-badge-success badge-compact">
                            Enabled
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
